type TFunction = (key: string, opts?: { defaultValue?: string }) => string;

const SCHEMA_CHIP_GROUPS = [
  'categories',
  'transactionTypes',
  'leisureAreas',
  'facilities',
  'securityItems',
  'condoAmenities',
  'condoSecurity',
] as const;

const FORM_OPTION_CHIP_GROUPS = [
  'condoIncludedExpenses',
  'rentalIncludedExpenses',
  'filterAmenities',
  // Furnishing is a property form option, and the client preferences reuse
  // its three tokens — without this group `semi_furnished` reaches the chip
  // as the humanized "semi furnished".
  'furnished',
] as const;

/**
 * Humanizes a raw schema/option token for display fallback.
 *
 * @param value - Stored chip value
 * @returns Readable label without i18n
 */
export function humanizeChipValue(value: string): string {
  return value.includes('_') ? value.replaceAll('_', ' ') : value;
}

/**
 * Looks up a translated label across a set of i18n groups sharing a key prefix.
 *
 * @param t - i18n translator bound to features/properties
 * @param prefix - Shared key prefix (e.g. "schema" or "form.options")
 * @param groups - Group names to try under the prefix, in order
 * @param value - Stored token to translate
 * @returns Localized label, or undefined if no group resolved it
 */
function translateFromGroups(
  t: TFunction,
  prefix: string,
  groups: readonly string[],
  value: string,
): string | undefined {
  for (const group of groups) {
    const key = `${prefix}.${group}.${value}`;
    const label = t(key, { defaultValue: '' });
    if (label && label !== key) return label;
  }
  return undefined;
}

/**
 * Resolves a translated chip label from schema.* then form.options.*.
 *
 * @param t - i18n translator bound to features/properties
 * @param value - Stored amenity/expense token
 * @returns Localized label, or a humanized fallback
 */
export function translatePropertyChipLabel(t: TFunction, value: string): string {
  return (
    translateFromGroups(t, 'schema', SCHEMA_CHIP_GROUPS, value) ??
    translateFromGroups(t, 'form.options', FORM_OPTION_CHIP_GROUPS, value) ??
    humanizeChipValue(value)
  );
}
